// import { faTrashCan } from "@jewlr/pro-light-svg-icons/faTrashCan"
import loadable from "@loadable/component"
import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import { Fragment } from "react"
import { connect } from "react-redux"
import { replace } from "redux-first-history"
import URI from "urijs"

import { fetchStylecode, updateChoice } from "areas/product/store/actions"
import { openModal } from "components/modal"
import Button from "components/ui/button"
// import FontAwesomeIcon from "components/ui/font-awesome-icon"
import Image from "components/ui/image"
import Shimmer from "components/ui/shimmer"
import { retry, isTestMode } from "helpers/application"
import { to_currency } from "helpers/number"
import { imageLookup } from "helpers/product/image-lookup"
import { useNavigation } from "helpers/use-navigation"
import { cn } from "styles/utils"

const StoneOptionsModal = loadable(
  () => retry(() => import("../modals/stone_options_modal")),
  { ssr: false }
)

const StoneCardsGrid = ({
  // canDecrement,
  choices,
  handleCardClick,
  // handleDelete,
  stonePrefs,
  wizardData,
}) => {
  const keyboardProps = useNavigation({ direction: "both" })

  const getStoneImage = (stonePref) => {
    const choice = choices[stonePref]

    if (!choice || !choice.imageKey) {
      return {
        x1: require("images/icons/jewlr.gif"),
        x2: require("images/icons/jewlr.gif"),
      }
    }

    return imageLookup(stonePref, choice.imageKey)
  }

  const getStoneName = (stonePref) => {
    const choice = choices[stonePref]

    if (!choice || !choice.value || !choice.title) {
      return "Select Stone"
    }

    return (choice.stoneName || choice.title).replace("(Simulated)", "").trim()
  }

  const getStonePrice = (stonePref) => {
    const choice = choices[stonePref]

    if (!choice || choice.price == null) {
      return null
    }

    return to_currency(choice.price)
  }

  const getStoneCategory = (stonePref) => {
    const choice = choices[stonePref]
    const data = wizardData[stonePref]

    if (!choice || !choice.jewlrValue || !data?.categories) {
      return null
    }

    const category = data.categories.find((cat) =>
      cat.options.some((opt) => opt.jewlrValue === choice.jewlrValue)
    )

    return category?.title?.replace("Stones", "").trim() || null
  }

  const getOptionTitle = (stonePref) => {
    const data = wizardData[stonePref]
    return data?.heading.title || null
  }

  return (
    <div className="tablet:grid-cols-4 grid grid-cols-3 gap-12" role="group">
      {stonePrefs.map((stonePref, index) => {
        const image = getStoneImage(stonePref)
        const stoneName = getStoneName(stonePref)
        const stoneCat = getStoneCategory(stonePref)
        const stonePrice = getStonePrice(stonePref)

        return (
          <Fragment key={`stone-card-${stonePref}`}>
            <div
              aria-label={`${getOptionTitle(stonePref) || `Stone ${index + 1}`} - ${stoneName}`}
              className={cn(
                "border-grey-10 relative flex cursor-pointer flex-col rounded-sm border bg-white px-8 py-10 transition-all",
                "focus:border-selected focus:outline-none"
              )}
              data-cy={`stone-card-${stonePref}`}
              onClick={() => handleCardClick(stonePref)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  handleCardClick(stonePref)
                }
              }}
              role="button"
              tabIndex={0}
              {...keyboardProps}
            >
              <div className="flex justify-between">
                <h3 className="text-12 mb-12 font-semibold">
                  {getOptionTitle(stonePref) || `Stone ${index + 1}`}
                </h3>

                {/* {canDecrement && (
                  <button
                    aria-label={`Delete stone ${index + 1}`}
                    className="bg-grey-4 flex h-20 w-20 items-center justify-center rounded border-0"
                    data-cy={`delete-stone-${stonePref}`}
                    onClick={(e) => handleDelete(e, stonePref)}
                    type="button"
                  >
                    <FontAwesomeIcon
                      className="text-primary size-12"
                      icon={faTrashCan}
                    />
                  </button>
                )} */}
              </div>

              <div className="mb-12 flex flex-1 items-center justify-center">
                <Image
                  alt={stoneName}
                  className="h-40 w-40 object-contain"
                  data-sizes="80px"
                  height="40"
                  src={image.x1}
                  srcSet={`${image.x1} 1x, ${image.x2} 2x`}
                  width="40"
                />
              </div>

              <div className="text-12 mb-8 flex flex-col items-center text-center">
                <p>{stoneName}</p>
                {stoneCat && <p>({stoneCat})</p>}
                {stonePrice && <p className="mt-6 font-bold">{stonePrice}</p>}
              </div>

              <Button
                className="text-12"
                onClick={() => handleCardClick(stonePref)}
                text={true}
                type="button"
                underline={true}
                variant="secondary"
              >
                Edit stone
              </Button>
            </div>
            <StoneOptionsModal pref={stonePref} />
          </Fragment>
        )
      })}
    </div>
  )
}

StoneCardsGrid.propTypes = {
  // canDecrement: PropTypes.bool,
  choices: PropTypes.object,
  handleCardClick: PropTypes.func,
  // handleDelete: PropTypes.func,
  stonePrefs: PropTypes.array,
  wizardData: PropTypes.object,
}

const InternalStoneCards = ({
  // canDecrement,
  choices,
  isLoadingStylecode,
  maxNumberOfStones,
  // removeStone,
  stonePrefs,
  prefStore,
  wizardData,
}) => {
  if (!prefStore || !maxNumberOfStones || maxNumberOfStones < 1) {
    return (
      <div className="border-b-grey-10 border-b py-20">
        <Shimmer
          className={cn(
            "h-(--base-height) w-full rounded-sm",
            "tablet:h-(--tablet-height)"
          )}
          style={{
            "--base-height": "180px",
            "--tablet-height": "120px",
          }}
        />
      </div>
    )
  }

  const handleCardClick = (stonePref) => {
    if (!isTestMode) {
      dataLayer.push({
        event: "clicked-edit-stone",
      })
    }
    openModal(`#stone-picker-${stonePref}`)
  }

  // const handleDelete = (e, stonePref) => {
  //   e.stopPropagation()
  //   if (!isTestMode) {
  //     dataLayer.push({
  //       event: "clicked-remove-stone",
  //       stonePref: stonePref,
  //     })
  //   }
  //   removeStone(stonePref)
  // }

  return (
    <div className="border-b-grey-10 tablet:px-6 border-b py-20">
      {isLoadingStylecode ? (
        <div className="tablet:grid-cols-4 grid grid-cols-3 gap-12">
          {[...Array(maxNumberOfStones)].map((_, index) => (
            <div
              className="border-grey-20 flex flex-col rounded-sm border bg-white px-8 py-10"
              key={`loading-${index}`}
            >
              <Shimmer
                className="mb-12"
                style={{ height: "16px", width: "60px" }}
              />
              <div className="mb-12 flex flex-1 items-center justify-center">
                <Shimmer
                  className="h-40 w-40"
                  style={{ borderRadius: "4px" }}
                />
              </div>
              <div className="mb-8 flex flex-col items-center text-center">
                <Shimmer style={{ height: "16px", width: "100%" }} />
              </div>
              <Shimmer style={{ height: "16px", width: "80px" }} />
            </div>
          ))}
        </div>
      ) : (
        <FocusScope>
          <StoneCardsGrid
            // canDecrement={canDecrement}
            choices={choices}
            handleCardClick={handleCardClick}
            // handleDelete={handleDelete}
            stonePrefs={stonePrefs}
            wizardData={wizardData}
          />
        </FocusScope>
      )}
    </div>
  )
}

InternalStoneCards.propTypes = {
  // canDecrement: PropTypes.bool,
  choices: PropTypes.object,
  isLoadingStylecode: PropTypes.bool,
  maxNumberOfStones: PropTypes.number,
  prefStore: PropTypes.bool,
  // removeStone: PropTypes.func,
  stonePrefs: PropTypes.array,
  wizardData: PropTypes.object,
}

const mapStateToProps = (state) => {
  const o1Data = state.wizard.data.o1
  const scData = state.wizard.data.sc
  const o1Choice = state.choices.o1
  const scChoice = state.choices.sc

  const stonePrefs = Object.keys(state.wizard.data).filter((pref) => {
    if (!/^s\d+$/.test(pref)) return false
    if (scData && scChoice) {
      return !state.choices[pref]?.disabled
    }
    return true
  })

  let canDecrement = false

  if (o1Data && o1Choice) {
    const availableOptions = o1Data.options.filter((opt) => !opt.disabled)
    const currentIndex = availableOptions.findIndex(
      (opt) => opt.style_code === o1Choice.style_code
    )
    canDecrement = currentIndex > 0
  } else if (scData && scChoice) {
    const currentValue = parseInt(scChoice.value, 10)
    const minStones = scData.min_stones || 1
    canDecrement = currentValue > minStones
  }

  return {
    canDecrement,
    choices: state.choices,
    location: state.router.location,
    o1Choice,
    o1Data,
    prefStore: state.wizardUI.prefStore,
    scChoice,
    scData,
    stonePrefs,
    wizardData: state.wizard.data,
  }
}

const mapDispatchToProps = (dispatch) => ({
  dispatch,
})

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const { o1Data, o1Choice, scData, scChoice, location } = stateProps
  const { dispatch } = dispatchProps
  const { setIsLoadingStylecode, maxNumberOfStones } = ownProps

  return {
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
    removeStone: (stonePref) => {
      const stoneNumber = parseInt(stonePref.replace("s", ""), 10)

      const { pathname, search, hash } = location

      const uri = URI(`${pathname}${search}${hash}`)
      const query = uri.search(true)

      for (let i = stoneNumber; i < maxNumberOfStones; i++) {
        const currentPref = `s${i}`
        const nextPref = `s${i + 1}`

        if (query[nextPref]) {
          query[currentPref] = query[nextPref]
        } else {
          delete query[currentPref]
        }
      }

      if (o1Data && o1Choice) {
        const availableOptions = o1Data.options.filter((opt) => !opt.disabled)
        const currentIndex = availableOptions.findIndex(
          (opt) => opt.style_code === o1Choice.style_code
        )

        if (currentIndex > 0) {
          const previousOption = availableOptions[currentIndex - 1]
          setIsLoadingStylecode && setIsLoadingStylecode(true)

          uri.search(query)
          const newUrl = uri.toString()
          dispatch(replace(newUrl))

          dispatch(fetchStylecode(previousOption.style_code))
        }
      } else if (scData && scChoice) {
        const currentValue = parseInt(scChoice.value, 10)
        const minStones = scData.min_stones || 1

        if (currentValue > minStones) {
          const newValue = currentValue - 1

          const newOption = scData.options.find(
            (opt) => opt.value === newValue.toString()
          )

          if (newOption) {
            query.sc = newValue.toString()

            uri.search(query)
            const newUrl = uri.toString()
            dispatch(replace(newUrl))

            dispatch(updateChoice("sc", newOption, true))
          }
        }
      }
    },
  }
}

const StoneCards = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(InternalStoneCards)

export default StoneCards
